import { FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { parseJWT } from "Auth/utils";
import { useBandContext } from "Bands/contexts";
import {
  customAccommodationRoomsSelectorForBlock,
  customAccommodationRoomTypesSelectorCreator,
  dataMetaSelector,
  eventBlockSelectorCreator,
} from "Bands/state";
import { dispatchGetEventAccommodationReservations } from "Bands/state/data/actions/eventBlocks/eventAccommodationReservation";
import { dispatchGetEventAccommodationRoomsForBlock } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoom";
import { dispatchGetEventAccommodationRoomTypes } from "Bands/state/data/actions/eventBlocks/eventAccommodationRoomType";
import { accommodationReservationSelectorCreator } from "Bands/state/data/selectors/accommodationReservation";
import {
  AccommodationRoomType,
  AccommodationType,
  AmenityType,
  BAND_ACTIONS,
  EventBlocks,
  IAccommodationBlock,
  IAccommodationReservation,
  IAccommodationRoom,
  IAccommodationRoomType,
} from "Bands/types";
import { useBandRegionalSettings, usePagination } from "Bands/utils";
import { copy } from "shared/assets";
import { ArrowRight, Checkmark } from "shared/assets/icons";
import {
  AddressInfo,
  Card,
  Loader,
  MainTeaser,
  Message,
  StaticMap,
  TableSkeleton,
  Title,
} from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import {
  BoldText,
  CenteredText,
  Icon,
  ItalicText,
  MutedText,
  TinyText,
} from "shared/styles";
import { colors } from "shared/styles/constants";
import { IAPIState, ICardTheme, ITheme } from "shared/types";
import { formatDateTime, formatTime, mapUserPermissions } from "shared/utils";
import { getImpersonateCookie } from "shared/utils/getImpersonateCookie";
import { AccommodationReservationTable } from "../AccommodationReservationTable/AccommodationReservationTable";
import { NotFlexSpacer } from "../EventSchedule/styles";
import {
  StyledAccommodationBlock,
  StyledAccommodationPeopleBlock,
  StyledAccommodationRoomsBlock,
  StyledAccommodationRow,
  StyledMap,
  StyledPersonalReservationTeaser,
} from "./styles";

interface Props {
  blockId: string;
  showPersonalInfo?: boolean;
  isPrint?: boolean;
}

const { accommodationBlock: blockCopy } = copy;

export const EventAccommodationBlock: FC<Props> = ({
  blockId,
  showPersonalInfo = true,
  isPrint,
}) => {
  const dispatch = useDispatch();
  const blockSelector = eventBlockSelectorCreator(
    blockId,
    EventBlocks.accommodation
  );

  const block = useSelector(blockSelector) as IAccommodationBlock;
  const { dateFormat, timeFormat } = useBandRegionalSettings();

  const { api: getRoomsMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.getEventAccommodationRoomsForBlock)
  );

  const formattedDate = (date: string) =>
    formatDateTime(date, {
      dateOnly: true,
      dateFormat,
    });

  const formattedNightsLabel = () => {
    if (!block.checkInDate || !block.checkOutDate) return;

    const checkIn = new Date(block.checkInDate);
    const checkOut = new Date(block.checkOutDate);

    const result = Math.ceil(
      (checkOut.getTime() - checkIn.getTime()) / (1000 * 3600 * 24)
    );

    return result + " " + blockCopy.view.night + (result > 1 ? "s" : "");
  };

  const formattedTime = (time: string) => formatTime({ time, timeFormat });

  const { currentBandUser, findBandUser, findBandUserByUserId } =
    useBandContext();

  const pagination = usePagination(
    API_ENDPOINTS.EVENT_ACCOMMODATION_BLOCK(block.id)
  );

  const reservationsSelector = useMemo(() => {
    const { pages, currentPage } = pagination;

    return accommodationReservationSelectorCreator(pages?.[currentPage]);
  }, [pagination]);

  const reservations = useSelector(reservationsSelector);

  const customAccommodationRoomTypesSelector = useMemo(
    () => customAccommodationRoomTypesSelectorCreator(block.id),
    [block.id]
  );

  const accommodationRoomTypes = useSelector(
    customAccommodationRoomTypesSelector
  );

  const roomsSelector = useMemo(
    () => customAccommodationRoomsSelectorForBlock(),
    []
  );

  const rooms = useSelector(roomsSelector);

  const personalReservationInfo = useMemo(() => {
    if (!showPersonalInfo) return null;

    const impersonate = parseJWT(getImpersonateCookie());

    const impersonateUser =
      impersonate && findBandUserByUserId(impersonate.username);

    const userId = impersonateUser ? impersonateUser.id : currentBandUser?.id!;

    const res = reservations.find(
      (reservation: IAccommodationReservation) =>
        reservation.bandUser.id === API_ENDPOINTS.BAND_USER(userId)
    );

    if (!res) return null;

    if (!res.roomType || rooms.length === 0)
      return {
        remark: res.remark,
      };

    const roomTypeName = accommodationRoomTypes.find(
      (roomType: IAccommodationRoomType) =>
        API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM_TYPE(roomType.id) ===
        res?.roomType?.id
    );

    const ownRoom: IAccommodationRoom = rooms.filter(
      (room) => API_ENDPOINTS.EVENT_ACCOMMODATION_ROOM(room.id) === res.room?.id
    )[0];

    if (!ownRoom)
      return {
        roomTypeName:
          AccommodationRoomType[
            roomTypeName?.name as keyof typeof AccommodationRoomType
          ] || roomTypeName?.name,
        remark: res.remark,
      };

    const shareMembers = ownRoom.people
      .filter((member) => member.id !== API_ENDPOINTS.BAND_USER(userId))
      .map((member) => {
        return findBandUser(member.id)?.firstname;
      });

    return {
      roomTypeName:
        AccommodationRoomType[
          roomTypeName?.name as keyof typeof AccommodationRoomType
        ] || roomTypeName?.name,
      remark: res.remark,
      shareMembers,
    };
  }, [
    reservations,
    currentBandUser?.id,
    accommodationRoomTypes,
    findBandUser,
    rooms,
    showPersonalInfo,
    findBandUserByUserId,
  ]);

  const fetchAccommodationInfo = useCallback(() => {
    dispatchGetEventAccommodationRoomTypes(dispatch, block.id);
    dispatchGetEventAccommodationRoomsForBlock(dispatch, block.id);
  }, [block.id, dispatch]);

  const fetchAccommodationReservations = useCallback(() => {
    dispatchGetEventAccommodationReservations({
      dispatch,
      blockId: block.id,
      itemsPerPage: 50,
      isRecursive: isPrint,
    });
  }, [block.id, dispatch, isPrint]);

  useEffect(() => {
    fetchAccommodationInfo();
  }, [fetchAccommodationInfo]);

  useEffect(() => {
    fetchAccommodationReservations();
  }, [fetchAccommodationReservations]);

  if (!block) return null;

  if (!block.address) {
    return <Message message={blockCopy.view.empty} />;
  }
  const userCanManage = mapUserPermissions(block.permissions || []).EDIT;

  return (
    <NotFlexSpacer size="l">
      <StyledAccommodationRow>
        <>
          <StyledAccommodationBlock>
            <Spacer size="m">
              {block.accommodationName && (
                <Title size="l">{block.accommodationName}</Title>
              )}
              <Spacer spaceDirection="horizontal" size="m" align="center">
                {block.accommodationType && (
                  <>
                    <Title size="s">
                      {
                        AccommodationType[
                          block.accommodationType as keyof typeof AccommodationType
                        ]
                      }
                    </Title>
                    <Spacer size="m">•</Spacer>
                  </>
                )}
                <AddressInfo
                  address={block.address}
                  format="country_last"
                  showFlag={false}
                ></AddressInfo>
              </Spacer>

              {block.amenities && block.amenities.length > 0 && (
                <Spacer spaceDirection="horizontal" size="s" align="center">
                  <Icon as={Checkmark} color={colors.green} />
                  <Spacer spaceDirection="horizontal" size="l" align="center">
                    {block.amenities.map((amenity, i) => [
                      i > 0 && ", ",
                      AmenityType[amenity as keyof typeof AmenityType],
                    ])}
                  </Spacer>
                </Spacer>
              )}

              <MainTeaser data-testid="accommodation_checkIn_teaser">
                <Spacer
                  size="m"
                  align="center"
                  spaceDirection="horizontal"
                  justifyContent="space-between"
                  flexWrap="wrap"
                >
                  <Spacer size="s" style={{ gridColumn: "span 2" }}>
                    <Title size="s" muted>
                      {blockCopy.view.checkIn}
                    </Title>

                    {block.checkInDate && (
                      <BoldText>{formattedDate(block.checkInDate)}</BoldText>
                    )}

                    {block.checkInTime && (
                      <TinyText>{formattedTime(block.checkInTime)}</TinyText>
                    )}
                  </Spacer>

                  <div style={{ gridColumn: "span 2" }}>
                    <Spacer size="xxs" align="center" justifyContent="center">
                      <CenteredText>
                        <TinyText as={MutedText}>
                          {formattedNightsLabel()}
                        </TinyText>
                      </CenteredText>
                      <Icon as={ArrowRight} color={colors.grey4} />
                    </Spacer>
                  </div>

                  <Spacer size="s" style={{ gridColumn: "span 2" }}>
                    <Title size="s" muted>
                      {blockCopy.view.checkOut}
                    </Title>

                    {block.checkOutDate && (
                      <BoldText>{formattedDate(block.checkOutDate)}</BoldText>
                    )}

                    {block.checkOutTime && (
                      <TinyText>{formattedTime(block.checkOutTime)}</TinyText>
                    )}
                  </Spacer>
                </Spacer>
              </MainTeaser>
            </Spacer>

            <StyledMap>
              <StaticMap
                coordinates={{
                  longitude: block.address.longitude,
                  latitude: block.address.latitude,
                }}
                alt={blockCopy.view.mapAlt}
                image={block.address.staticImage}
                width="180px"
                height="180px"
              />
            </StyledMap>
          </StyledAccommodationBlock>
          {getRoomsMeta?.status === IAPIState.pending ? (
            <Loader />
          ) : (
            personalReservationInfo && (
              <StyledPersonalReservationTeaser>
                <Spacer size="s">
                  <Spacer size="xs" spaceDirection="horizontal">
                    <BoldText>Your room:</BoldText>

                    <MutedText>
                      {personalReservationInfo.roomTypeName ? (
                        personalReservationInfo.roomTypeName
                      ) : (
                        <ItalicText>
                          {
                            blockCopy.create.personalReservationBlock.messages
                              .noRoomAssignedYet
                          }
                        </ItalicText>
                      )}
                    </MutedText>
                  </Spacer>
                  {personalReservationInfo.remark && (
                    <>
                      <BoldText>Your remarks:</BoldText>
                      <MutedText>{personalReservationInfo.remark}</MutedText>
                    </>
                  )}
                  {personalReservationInfo.shareMembers &&
                    personalReservationInfo.shareMembers.length > 0 && (
                      <>
                        <BoldText>Sharing a room with:</BoldText>
                        <MutedText>
                          {personalReservationInfo.shareMembers.join(", ")}
                        </MutedText>
                      </>
                    )}
                </Spacer>
              </StyledPersonalReservationTeaser>
            )
          )}
        </>
      </StyledAccommodationRow>

      {userCanManage && (
        <>
          <StyledAccommodationRoomsBlock size="l">
            <Title
              title={blockCopy.create.roomsBlock.title}
              size="m"
              asComponent="h3"
            />
            <Card theme={ICardTheme.secondary}>
              <Spacer size="l">
                {accommodationRoomTypes.length !== 0 ? (
                  accommodationRoomTypes.map((roomType) => (
                    <Spacer
                      size="xxs"
                      spaceDirection="horizontal"
                      key={roomType.id}
                    >
                      <Title
                        size="s"
                        title={
                          AccommodationRoomType[
                            roomType?.name as keyof typeof AccommodationRoomType
                          ] || roomType?.name
                        }
                        asComponent="h4"
                      />{" "}
                      <MutedText>
                        (
                        {roomType.peoplePerRoom * roomType.quantity -
                          roomType.vacancy}
                        /{roomType.peoplePerRoom * roomType.quantity} beds
                        occupied)
                      </MutedText>
                    </Spacer>
                  ))
                ) : (
                  <Message
                    message={blockCopy.create.roomsBlock.messages.empty}
                  />
                )}
              </Spacer>
            </Card>
          </StyledAccommodationRoomsBlock>
          <StyledAccommodationPeopleBlock size="l">
            <Title
              title={blockCopy.create.peopleBlock.title}
              size="m"
              asComponent="h3"
              printOptions={{
                display: "none",
              }}
            />

            {getRoomsMeta?.status === IAPIState.pending ? (
              <TableSkeleton nbRows={5} theme={ITheme.tertiary} />
            ) : (
              <AccommodationReservationTable
                block={block}
                fetchAccommodationReservations={fetchAccommodationReservations}
                showActions={false}
              ></AccommodationReservationTable>
            )}
          </StyledAccommodationPeopleBlock>
        </>
      )}
    </NotFlexSpacer>
  );
};
